/** Deterministic halves of the AI features — free, auditable, always available.
 *  §5.4 amendment 4: rules first, model second. §5.7: flags are advisory, never blocking. */
import type { DocumentRecord, InventoryCat, InventoryItem, Job, PriceItem, TakeoffLine } from '../core/types';
import type { ResolvedLine } from '../docs/compute';
import { computeBid } from '../docs/compute';

/** scan category → price book candidates the AI may choose among (never outside them) */
export const CANDIDATES: Record<InventoryCat, string[]> = {
  turf: ['overseed', 'sod', 'turfart', 'grade'],
  bed: ['bed', 'mulch', 'dg', 'rock', 'topdress'],
  tree: ['trimtree', 'treeremove', 'tree24', 'tree15', 'stump'],
  palm: ['palm'],
  shrub: ['shrub'],
  cactus: ['cactus'],
  hedge: ['edge', 'shrub'],
  valve: ['valve', 'irrchk'],
  drain: [],
  other: [],
};

export function bidMapCandidates(inventory: InventoryItem[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const i of inventory) {
    if (i.deletedAt) continue;
    if (!out[i.cat]) out[i.cat] = CANDIDATES[i.cat] ?? [];
  }
  return out;
}

// ---------- §5.7 bid sanity check — pure rules over prior issued proposals ----------

export interface SanityFlag { kind: string; text: string }

export const MIN_HISTORY = 10;

export interface SanityInputs {
  job: Job;
  lines: TakeoffLine[];
  resolved: ResolvedLine[];
  prices: PriceItem[];
  /** issued proposals from OTHER jobs, i.e. the history */
  history: DocumentRecord[];
}

export function sanityCheck(i: SanityInputs): { flags: SanityFlag[]; enoughHistory: boolean } {
  const flags: SanityFlag[] = [];
  const bid = computeBid(i.resolved, i.job.fin);
  const lot = i.job.parcel?.lotSqFt;

  // rules that need no history at all
  const areaSqFt = i.resolved.filter((l) => l.unit === 'sq ft').reduce((s, l) => s + l.qty, 0);
  if (lot && areaSqFt > lot * 1.15) {
    flags.push({
      kind: 'area_exceeds_lot',
      text: `Measured areas total ${Math.round(areaSqFt).toLocaleString('en-US')} sq ft — more than the ${lot.toLocaleString('en-US')} sq ft lot. Something is double-drawn or over-entered.`,
    });
  }
  const zeroQty = i.resolved.filter((l) => l.qty === 0);
  if (zeroQty.length) {
    flags.push({ kind: 'zero_qty', text: `${zeroQty.length} line(s) have quantity 0 and add nothing: ${zeroQty.map((l) => l.name).join(', ')}.` });
  }
  if (bid.math.totalCents <= 0 && i.resolved.length > 0) {
    flags.push({ kind: 'non_positive_total', text: 'The total is zero or negative. Check the adjustment.' });
  }
  if (i.job.fin.markupPct === 0 && bid.math.subtotalCents > 500000) {
    flags.push({ kind: 'zero_margin', text: 'Overhead/markup is 0% on a five-figure bid. Confirm that is intentional.' });
  }

  // history-based rules (§12.8: silent until enough comparable bids exist)
  const hist = i.history.filter((d) => d.type === 'proposal' && d.status === 'issued');
  const enoughHistory = hist.length >= MIN_HISTORY;
  if (enoughHistory && lot) {
    const perSqFt = hist
      .map((d) => ({ t: d.payload.math.totalCents, lot: d.payload.parcel?.lotSqFt }))
      .filter((x): x is { t: number; lot: number } => !!x.lot && x.lot > 0)
      .map((x) => x.t / x.lot);
    if (perSqFt.length >= MIN_HISTORY) {
      const sorted = [...perSqFt].sort((a, b) => a - b);
      const lo = sorted[Math.floor(sorted.length * 0.1)]!;
      const hi = sorted[Math.ceil(sorted.length * 0.9) - 1]!;
      const mine = bid.math.totalCents / lot;
      if (mine < lo * 0.8) flags.push({ kind: 'price_low', text: `This bid runs $${(mine / 100).toFixed(2)}/sq ft of lot — below the historical band ($${(lo / 100).toFixed(2)}–$${(hi / 100).toFixed(2)}).` });
      if (mine > hi * 1.2) flags.push({ kind: 'price_high', text: `This bid runs $${(mine / 100).toFixed(2)}/sq ft of lot — above the historical band ($${(lo / 100).toFixed(2)}–$${(hi / 100).toFixed(2)}).` });
    }
    // line items most similar jobs included but this one lacks
    const mineItems = new Set(i.resolved.map((l) => l.name));
    const counts = new Map<string, number>();
    for (const d of hist) for (const l of d.payload.lines) counts.set(l.name, (counts.get(l.name) ?? 0) + 1);
    for (const [name, n] of counts) {
      if (n / hist.length >= 0.7 && !mineItems.has(name)) {
        flags.push({ kind: 'missing_common', text: `${Math.round((n / hist.length) * 100)}% of past proposals include “${name}” — this one doesn’t. Deliberate?` });
      }
    }
  }
  return { flags, enoughHistory };
}

// ---------- §12.6 survey import — pure, no AI ----------

export interface SurveyReport {
  inventoryAdded: number;
  linesAdded: number;
  skipped: { item: string; reason: string }[];
  address?: string;
  notes?: string;
}

export function matchPriceItem(nameOrId: string, prices: PriceItem[]): PriceItem | null {
  const q = nameOrId.trim().toLowerCase();
  if (!q) return null;
  const active = prices.filter((p) => !p.retired);
  return active.find((p) => p.itemId.toLowerCase() === q)
    ?? active.find((p) => p.name.toLowerCase() === q)
    ?? active.find((p) => p.name.toLowerCase().includes(q))
    ?? active.find((p) => q.includes(p.itemId.toLowerCase()) && p.itemId.length >= 3)
    ?? null;
}

const CATS: InventoryCat[] = ['tree', 'palm', 'shrub', 'cactus', 'hedge', 'bed', 'turf', 'valve', 'drain', 'other'];

export interface ParsedSurvey {
  inventory: { cat: InventoryCat; species?: string; qty: number; cond: 'good' | 'fair' | 'poor' | 'dead' | 'unknown'; notes?: string }[];
  takeoff: { price: PriceItem; qty: number; label: string }[];
  skipped: { item: string; reason: string }[];
  address?: string;
  notes?: string;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function parseSurvey(text: string, prices: PriceItem[]): ParsedSurvey {
  let obj: any;
  try { obj = JSON.parse(text); } catch { throw new Error('That is not JSON. Paste the whole survey block, starting with {.'); }
  if (obj?.app !== 'landscaping-bid-studio-survey') throw new Error('That JSON is not a Bid Studio survey (missing the survey marker).');
  const out: ParsedSurvey = { inventory: [], takeoff: [], skipped: [], address: obj.address ? String(obj.address) : undefined, notes: obj.notes ? String(obj.notes) : undefined };
  for (const r of Array.isArray(obj.inventory) ? obj.inventory : []) {
    const qty = Number(r?.qty);
    if (!Number.isFinite(qty) || qty <= 0) { out.skipped.push({ item: String(r?.species || r?.cat || '?'), reason: 'no usable quantity' }); continue; }
    out.inventory.push({
      cat: CATS.includes(r?.cat) ? r.cat : 'other',
      species: r?.species ? String(r.species) : undefined,
      qty,
      cond: ['good', 'fair', 'poor', 'dead', 'unknown'].includes(r?.cond) ? r.cond : 'unknown',
      notes: r?.notes ? String(r.notes) : undefined,
    });
  }
  for (const r of Array.isArray(obj.takeoff) ? obj.takeoff : []) {
    const qty = Number(r?.qty);
    const raw = String(r?.item ?? '');
    if (!Number.isFinite(qty) || qty <= 0) { out.skipped.push({ item: raw || '?', reason: 'no usable quantity' }); continue; }
    const p = matchPriceItem(raw, prices);
    if (!p) { out.skipped.push({ item: raw || '?', reason: 'no matching price book item' }); continue; }
    out.takeoff.push({ price: p, qty, label: r?.label ? String(r.label) : p.name });
  }
  return out;
}
